<template>
  <div class="app-container-wrapper">
    <head-container :project-name="projectName">
      <breadcrumb :project-name="projectName" />
    </head-container>

    <div class="app-container has-title">
      <!-- 项目基本信息 -->
      <div v-for="item in optionsList" :key="item.title" class="card">
        <div class="section">
          <div class="section-title">
            <base-section-title :title="item.title" />
          </div>
          <div class="section-content">
            <jczh-descriptions
              :info="entrustInfo"
              :option="item.option"
              :el-attrs="{
                size: 'medium',
                labelStyle: {
                  whiteSpace: 'nowrap'
                }
              }"
            >
              <template v-slot:budget="{ value }">
                <span>{{ strToThousands(value || '-') }}</span>
                <span class="m-l-10">万元</span>
                <money-in-cn :money="value * 10000" class="m-l-10" />
              </template>

              <template v-slot:upload="{ value }">
                <upload
                  v-if="value"
                  :group-id-before="value"
                  :props-data="{
                    disabled: true
                  }"
                />
                <span v-else>-</span>
              </template>
            </jczh-descriptions>
          </div>
        </div>
      </div>

      <!-- 委托代理机构信息 -->
      <div class="card">
        <div class="section">
          <div class="section-title">
            <base-section-title title="代理方" />
          </div>
          <div class="section-content">
            <el-row :gutter="40">
              <el-col :sm="24" :xl="12">
                代理方：
                <span>{{ agentInfo.agentCompanyName || ' - ' }}</span>
                <span class="m-l-20">
                  {{ agentInfo.acceptEmployeeName || ' - ' }}
                </span>
                <span class="m-l-10">
                  {{ agentInfo.acceptEmployeeMobile || ' - ' }}
                </span>
                <span class="m-l-10">
                  {{ agentInfo.acceptEmployeeDeptName || ' - ' }}
                </span>
              </el-col>
              <!-- <el-col v-if="!isJxic" :sm="24" :xl="12"> -->
              <el-col :sm="24" :xl="12">
                企业供应商库：
                {{
                  isAllowUseSrmDict.translate(entrustInfo.isAllowUseSrm)
                    .label || '-'
                }}
              </el-col>
            </el-row>
          </div>
        </div>
      </div>

      <slot name="status" :data="statusInfo"></slot>

      <slot
        v-if="
          Number(entrustInfo.acceptStatus) ===
            bidAcceptStatusDict.ACCEPTED.value
        "
        name="project"
        :data="projectInfo"
      ></slot>

      <slot name="operate" :data="operateInfo"></slot>
    </div>
  </div>
</template>

<script>
import Breadcrumb from '@/components/common/Breadcrumb/Breadcrumb'
import BaseSectionTitle from '@/components/common/BaseSectionTitle'
import Upload from '@/components/common/Upload/index'
import MoneyInCn from '@/components/common/MoneyInCn'
import HeadContainer from '@/components/common/HeadContainer'

import { fetchEntrustDetailApi } from '@/api/procure/entrust'
import { getAgentEntrustProjectNumApi } from '@/api/procure/entrust'

import {
  projectAssetDisposalMethodDict,
  projectTypeDict
} from '@/dictionary/project/common'
import { bidAcceptStatusDict } from '@/dictionary/project/bid'
import {
  registerInvestTypeDict,
  registerIsFocusPurchaseDict,
  isRegisterTypeDict
} from '@/dictionary/project/register'
import { isAllowUseSrmDict } from '@/dictionary/supplier-management/supplier-lib'

import { strToThousands } from '@/utils/utils'
import { mapGetters } from 'vuex'

export default {
  name: 'EntrustDetail',

  components: {
    Breadcrumb,
    BaseSectionTitle,
    Upload,
    MoneyInCn,
    HeadContainer
  },

  data() {
    return {
      projectTypeDict, // 项目类型字典
      bidAcceptStatusDict,
      isAllowUseSrmDict,

      projectId: this.$route.params.projectId || '',

      entrustInfo: {}, // 委托详情表单数据

      // 手动配置面包屑列表
      breadcrumb: [{ title: '委托详情' }],

      biddingProjectNumbs: 0, // 招标项目统计数量
      inquiryProjectNumbs: 0, // 询比项目统计数量
      competitionProjectNumbs: 0, // 竞价项目统计数量
      negotiateProjectNumbs: 0 // 谈判采购项目统计数量
    }
  },

  computed: {
    ...mapGetters('company', ['platformTitle']),

    // 是否是省投及其下属企业，且是在采购工作台
    isJxic() {
      return this.platformTitle === '江西投资集团电子采购平台'
    },

    // 代理方信息
    agentInfo() {
      return this.entrustInfo.agentInfo || {}
    },

    // 委托项目名称
    projectName() {
      return this.entrustInfo.projectName
    },

    // 委托状态信息
    statusInfo() {
      return {
        acceptStatus: this.entrustInfo.acceptStatus,
        rejectRemark: this.entrustInfo.acceptInfo?.rejectRemark,
        publishTime: this.entrustInfo.publishTime,
        acceptTime: this.entrustInfo.acceptInfo?.acceptTime
      }
    },

    // 操作委托需要的信息
    operateInfo() {
      return {
        projectId: this.entrustInfo.projectId,
        projectName: this.projectName,
        acceptStatus: this.entrustInfo.acceptStatus
      }
    },

    // 项目统计信息
    projectInfo() {
      return {
        projectName: this.projectName,
        biddingProjectNumbs: this.biddingProjectNumbs,
        inquiryProjectNumbs: this.inquiryProjectNumbs,
        competitionProjectNumbs: this.competitionProjectNumbs,
        negotiateProjectNumbs: this.negotiateProjectNumbs
      }
    },

    // 是否是登记项目
    isRegisterProject() {
      return this.entrustInfo.isRegisterType === isRegisterTypeDict.YES.value
    },

    // 获取详情信息配置
    optionsList() {
      return [
        {
          title: '项目信息',
          option: [
            { label: '项目名称', prop: 'projectName' },
            { label: '项目编号', prop: 'projectCode' },
            { label: '项目概算', prop: 'budget', slotName: 'budget' },
            {
              label: '项目类型',
              prop: 'projectType',
              filter: value => projectTypeDict.translate(value)?.label || ' - '
            },
            {
              label: '处置方式',
              prop: 'assetDisposalMethod',
              filter: value =>
                projectAssetDisposalMethodDict.translate(value)?.label ||
                projectAssetDisposalMethodDict.EXPENDITURE.label,
              hideFun: () =>
                this.entrustInfo.projectType !==
                projectTypeDict.ASSET_DISPOSAL.value
            },
            {
              label: '投资类型',
              prop: 'investmentType',
              filter: value =>
                registerInvestTypeDict.translate(value)?.label || ' - ',
              hideFun: () => !this.isRegisterProject
            },
            {
              label: '是否集中采购',
              prop: 'centralizedPurchaseType',
              filter: value =>
                registerIsFocusPurchaseDict.translate(value)?.label || ' - ',
              hideFun: () => !this.isRegisterProject
            },
            {
              label: '项目地点',
              prop: '',
              /* eslint-disable */
              filter: value =>
                this.entrustInfo.country === '中国'
                  ? `${this.entrustInfo.province + '-' || ''}${this.entrustInfo
                      .city + '-' || ''}${this.entrustInfo.area || ''}`
                  : this.entrustInfo.foreignAddress || ''
              /* eslint-enable */
            },
            { label: '详细地址', prop: 'address' },
            { label: '上传附件', prop: 'attachmentGroupId', slotName: 'upload' }
          ]
        },
        {
          title: '委托方信息',
          option: [
            {
              label: '委托单位名称',
              prop: 'purchaseCompanyName',
              filter: () => this.entrustInfo?.entrusterInfo?.purchaseCompanyName
            },
            {
              label: '联系人',
              prop: 'purchaseLink',
              filter: () => this.entrustInfo?.entrusterInfo?.purchaseLink
            },
            {
              label: '联系方式',
              prop: 'linkTelephone',
              filter: () => this.entrustInfo?.entrusterInfo?.linkTelephone
            }
          ]
        }
      ]
    }
  },

  async created() {
    await this.getFormDetail()
    if (
      Number(this.entrustInfo.acceptStatus) ===
      bidAcceptStatusDict.ACCEPTED.value
    ) {
      await this.getAgentEntrustProjectNum()
    }
  },

  methods: {
    strToThousands,

    /**
     * 获取表单详情
     */
    async getFormDetail() {
      try {
        const _data = await fetchEntrustDetailApi(this.projectId)
        this.entrustInfo = _data.data
        this.breadcrumb.unshift({ title: _data.data.projectName })
      } catch (err) {
        this.$message.error(err.message || '获取委托详情失败')
      }
    },

    /**
     * 刷新整个详情页
     */
    refreshPage() {
      this.getFormDetail()
    },

    /**
     * 获取项目统计信息
     */
    async getAgentEntrustProjectNum() {
      try {
        const _data = await getAgentEntrustProjectNumApi(this.projectId)
        this.biddingProjectNumbs = _data.data.bidding || 0
        this.inquiryProjectNumbs = _data.data.inquiry || 0
        this.competitionProjectNumbs = _data.data.competition || 0
        this.negotiateProjectNumbs = _data.data.negotiate || 0
      } catch (err) {
        this.$message.error(err.message || '获取委托详情失败')
      }
    }
  }
}
</script>
